import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface ExecutionSettings {
  EnforceDynamicData: boolean;
}

export interface Flowblueprint {
  Id: string;
  Name: string;
  Steps: FlowStep[];
  CustomData: Record<string, any>;
  ExecutionSettings?: ExecutionSettings;
  CategoryId?: string;
  Deleted?: boolean;
}

export interface FlowStep {
  StepId: string;
  StepType: string;
  StepAddress: string;
  CustomData: Record<string, any>;
}

@Injectable({
  providedIn: 'root'
})
export class FlowblueprintService {
  private api: AxiosInstance;
  private authService = inject(KeycloakAuthService);
  private apiConfig = inject(ApiConfigService);

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.api = axios.create({
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor to dynamically set baseURL and include OAuth token
    this.api.interceptors.request.use((config) => {
      // Dynamically construct baseURL on each request to get current tenantId
      if (isPlatformBrowser(this.platformId)) {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/${tenantId}`;
      }

      const token = this.authService.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  async getFlowblueprints(): Promise<Array<Flowblueprint> | null> {
    try {
      const response = await this.api.get('/flowblueprint');
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getFlowblueprint(id: string): Promise<Flowblueprint | null> {
    try {
      const response = await this.api.get('/flowblueprint/' + id);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async updateFlowblueprint(flowblueprint: Flowblueprint): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.put('/flowblueprint/' + flowblueprint.Id, flowblueprint);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async createFlowblueprint(flowblueprint: Flowblueprint): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.post('/flowblueprint', flowblueprint);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async deleteFlowblueprint(id: string): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.delete('/flowblueprint/' + id);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async restoreFlowblueprint(id: string): Promise<AxiosResponse | null> {
    try {
      // Assumes restore is exposed through PATCH until a dedicated restore endpoint is available.
      const response = await this.api.patch('/flowblueprint/' + id, { Deleted: false });
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }
}
